import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { Translation } from 'react-i18next';

import useMediaQuery from 'src/hooks/useMediaQuery';
import useCurrentCourse from '@src/hooks/useCurrentCourse';
import useForum from '@src/hooks/useForum';
import { setModalType } from '@src/store/actions';
import { getIsSignIn } from '@src/store/selectors';
import { formatToDate } from '@src/utils/time';
import { STATE_LOADING, MODAL_TYPE_LOGIN } from '@src/const/common';

import { NoMessageIcon } from '@src/components/common/icon';
import Header from '@src/components/common/header';
import Button from '@src/components/common/button';
import { StyledPagination } from '@src/components/common/style';
import Messages from './messages';
import InputMessage from './inputMessage';
import Operator from './operator';

import { BlockWrapper } from '../styled';

const getTextRows = (mediaMatch): number => {
	if (mediaMatch.isLgDesktop || mediaMatch.isDesktop || mediaMatch.isTablet) {
		return 1;
	}
	if (mediaMatch.isSmTablet) {
		return 2;
	}
	if (mediaMatch.isMobile) {
		return 4;
	}
	return 1;
};

const StyledInputMessage = styled(InputMessage)`
	align-items: center;
	padding: 20px 24px;
	margin-top: 32px;
	box-shadow: 0px 0px 12px 0px rgba(69, 65, 78, 0.09);

	@media ${props => props.theme.device.smTablet} {
		align-items: flex-start;
	}

	@media ${props => props.theme.device.mobile} {
		align-items: flex-start;
	}
`;

const StyledNoMessageIcon = styled(NoMessageIcon)`
	margin-top: 60px;
`;

const DiscussionsWrapper = styled(BlockWrapper)`
	background: ${props => props.theme.colors.block};
	padding-bottom: 40px;
`;

const Content = styled.div`
	display: grid;
	grid-row-gap: 16px;
	margin-top: 16px;
`;

const NoMessageInfo = styled.div`
	white-space: pre-line;
	text-align: center;
	${props => (props.isLoading ?
		'height: 480px;' : `
			display: inline-flex;
			flex-direction: column;
			align-items: center;
			width: 100%;
		`
	)}
	& > * {
		display: ${props => (props.isLoading ? 'none' : 'flex')};
	}
`;

const InfoText = styled.div`
	font-weight: 300;
	margin-top: 24px;
`;

const AddDiscussionsBeforeButton = styled(Button)`
	padding: 8px 32px;
	border: 1px solid ${props => props.theme.colors.primary};
	color: ${props => props.theme.colors.primary};
	margin-top: 24px;
	margin-bottom: 40px;
	background: #FFFFFF;
`;

const SpinnerWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const PaginationWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Discussions = (): JSX.Element => {
	const dispatch = useDispatch();
	const isSignedIn = useSelector(getIsSignIn) as boolean;
	const { t } = useTranslation();
	const [onlyMyMessage, setOnlyMeMessage] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const { currentCourse: { id } } = useCurrentCourse();
	const {
		discussionsBefore,
		discussionsBeforeTotal,
		handleAddCourseBeforeMessage,
		courseDiscussionsBeforeState,
		getCourseDiscussionsBefore,
	} = useForum({ courseId: id, onlyMe: onlyMyMessage });
	const hasMessage = discussionsBeforeTotal !== 0;
	const [isShowInputBox, setIsInputBox] = useState(false);
	const isLoading = courseDiscussionsBeforeState === STATE_LOADING;
	const { mediaMatch } = useMediaQuery();
	const { isLgDesktop, isDesktop, isTablet } = mediaMatch;
	const rows = getTextRows(mediaMatch);

	const handleToggleOnlyMyMessage = () => {
		setOnlyMeMessage(prev => !prev);
	};

	const handleOpenLoginModal = () => {
		dispatch(setModalType({ type: MODAL_TYPE_LOGIN }));
	};

	const handleShowAddCommentsBox = () => {
		setIsInputBox(true);
	};

	const handlePageChangeScroll = () => {
		const targetElement = document.getElementById('discussions');
		window.scrollTo({
			top: targetElement.offsetTop,
			behavior: 'smooth',
		});
	};

	const pageSize = 5;
	useEffect(() => {
		getCourseDiscussionsBefore({ pageNumber: pageNumber - 1, pageSize });
	}, [pageNumber]);

	const paginationConfig = {
		hideOnSinglePage: true,
		pageSize,
		total: discussionsBeforeTotal,
		current: pageNumber,
		showTotal: (total: number): JSX.Element | string => {
			if (isLgDesktop || isDesktop || isTablet) {
				return (
					<Translation>
						{tra => (
							tra('pagination:total_style_large', {
								thisPage: pageNumber,
								totalPage: Math.ceil((discussionsBeforeTotal || total) / pageSize),
								unit: tra('pagination:rating_page_unit'),
								total: (discussionsBeforeTotal || total),
							})
						)}
					</Translation>
				);
			}
			return '';
		},
		showSizeChanger: false,
		onChange: (currentPage) => {
			if (pageNumber !== currentPage) {
				setPageNumber(currentPage);
				handlePageChangeScroll();
			}
		},
	};

	return (
		<DiscussionsWrapper id="discussions">
			<Header text={t('course:information:discussions')} />
			<Spin
				indicator={(
					<SpinnerWrapper>
						<LoadingOutlined style={{ fontSize: 60, color: '#C4C4C4' }} spin />
					</SpinnerWrapper>
				)}
				spinning={isLoading}
			>
				<Operator
					checked={onlyMyMessage}
					handleOnClick={handleToggleOnlyMyMessage}
					isSignedIn={isSignedIn}
				/>
				{(hasMessage || isShowInputBox) && (
					<StyledInputMessage
						rows={rows}
						handleMessageSubmit={message => {
							handleAddCourseBeforeMessage({
								message,
								onSuccess: () => setIsInputBox(false),
							});
						}}
						handleOnFocus={isSignedIn ? null : handleOpenLoginModal}
						addMessageState={courseDiscussionsBeforeState}
						buttonPosition="right"
					/>
				)}
				{hasMessage && (
					<Content>
						{discussionsBefore.map(discussion => (
							<Messages
								key={discussion.id}
								commentId={discussion.id}
								message={discussion.message}
								user={discussion.user}
								createAt={formatToDate(discussion.created_at)}
								replies={discussion.replies}
							/>
						))}
						<PaginationWrapper>
							<StyledPagination {...paginationConfig} />
						</PaginationWrapper>
					</Content>
				)}
				{!hasMessage && !isShowInputBox && (
					<NoMessageInfo isLoading={isLoading}>
						<StyledNoMessageIcon />
						<InfoText>
							{t('course:discussions:no_message_info')}
						</InfoText>
						<AddDiscussionsBeforeButton
							handleClick={isSignedIn ? handleShowAddCommentsBox : handleOpenLoginModal}
						>
							{t('course:discussions:i_want_to_add_comments')}
						</AddDiscussionsBeforeButton>
					</NoMessageInfo>
				)}
			</Spin>
		</DiscussionsWrapper>
	);
};

export default Discussions;
